import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@radix-ui/react-select";
import { getConfig } from "@stryker/core/api/jobs";
import { createQuote, getQuote, getQuoteDataForCreateFlow, updateQuote } from "@stryker/core/api/quotes";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { NoteSchema } from "@stryker/core/types/quotes";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AdditionalNotes from "@/components/quotes/AdditionalNotes";
import ConfirmCreateQuote from "@/components/quotes/ConfirmCreateQuote";
import ContactInfo from "@/components/quotes/ContactInfo";
import QuoteDetailsTable from "@/components/quotes/QuoteDetailsTable";
import ScopeDescription from "@/components/quotes/ScopeDescription";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import useLocationQuery from "@/hooks/useLocationQuery";
import { type QuoteFormSchema, quoteFormSchema } from "@/lib/schemas/createUpdateQuoteSchema";
import { getMargin } from "@/utils/calculations";

export default function CreateUpdateQuote() {
  const [isConfirmCreationOpen, setIsConfirmCreationOpen] = useState(false);

  const [notes, setNotes] = useState<NoteSchema[]>([]);
  const [additional_notes, setAdditional_notes] = useState<string[]>([]);

  const addNote = () => {
    setNotes([...notes, { id: crypto.randomUUID(), note_name: "", custom_note: undefined }]);
  };

  const updateNote = (index: number, value: NoteSchema) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = value;
    setNotes(updatedNotes);
  };

  const removeNote = (index: number) => {
    const updatedNotes = notes.filter((_, i) => i !== index);
    setNotes(updatedNotes);
  };

  const query = useLocationQuery();
  const jobNumber = query.get("job_number");
  const quoteId = query.get("quote_id");
  const repairSystemId = query.get("repair_system");

  const { data: repairSystem } = useQuery({
    queryKey: [QueryKeys.JobsConfig],
    queryFn: getConfig,
    staleTime: 1000000,
    select: (response) => {
      return response.repair_systems.find((system) => {
        return String(system.id) === repairSystemId;
      });
    },
  });

  const { data: quote, isLoading: isLoadingQuote } = useQuery({
    queryKey: [QueryKeys.QuoteDetails, quoteId],
    queryFn: quoteId ? () => getQuote(quoteId) : skipToken,
  });

  const { data: initialQuoteData, isFetching: isFetchingInitialQuoteData } = useQuery({
    queryKey: [QueryKeys.QuoteDetails, jobNumber],
    queryFn: jobNumber && !quoteId ? () => getQuoteDataForCreateFlow(jobNumber) : skipToken,
  });

  useEffect(() => {
    if (quote?.additional_notes) {
      setAdditional_notes(quote.additional_notes.map((id) => String(id)));
    }
  }, [quote?.additional_notes]);

  useEffect(() => {
    if (quote?.new_notes && quote.new_notes.length > 0) {
      setNotes(
        quote.new_notes.map((note) => ({
          ...note,
          id: note.id ?? crypto.randomUUID(),
        }))
      );
    }
  }, [quote?.new_notes]);

  const form = useForm<QuoteFormSchema>({
    resolver: zodResolver(quoteFormSchema),
    values: {
      quoteDetails: quoteId
        ? (quote?.quote_details.map((detail) => ({
            ...detail,
            is_editable: detail.is_editable ?? true,
          })) ?? [])
        : (initialQuoteData?.calculation_res.map((res) => {
            return {
              item_name: res.item_name,
              quantity: res.quantity,
              custom_item_name: undefined,
              unit: res.unit,
              custom_unit: undefined,
              cost_per_unit: res.cost_per_unit ?? 0,
              price_per_unit: res.price_per_unit ?? 0,
              is_editable: res.is_editable,
            };
          }) ?? []),
      scopeDescription: quote?.scope_description ?? "",
      isQuoteLumpSum: quote?.is_lump_sum,
      costBreakdownEnabled: !!quote?.cost_breakdown,
      costBreakdown: quote?.cost_breakdown ?? {
        supervisorQuantity: 0,
        supervisorHours: 0,
        supervisorRate: 0,
        technicianQuantity: 0,
        technicianHours: 0,
        technicianRate: 0,
        equipment: 0,
        other: 0,
        workersCompEnabled: false,
      },
      totalRevenueOverride: quoteId ? (quote?.total_revenue_override ?? null) : null,
      contactInfo: {
        name: quote?.quote_name ?? "",
        email: quoteId ? (quote?.contact_info_email ?? "") : (initialQuoteData?.for_email ?? ""),
        salesman: quoteId ? (quote?.salesman ?? "") : (initialQuoteData?.salesman ?? ""),
      },
    },
    defaultValues: {
      quoteDetails: [],
    },
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: createQuoteMutate, isPending: isPendingCreateQuote } = useMutation({
    mutationFn: createQuote,
    onSuccess: (response) => {
      navigate(`/quotes/${response.id}?job_number=${jobNumber}`);
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.JobsConfig],
      });
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: error });
    },
  });

  const { mutate: updateQuoteMutate, isPending: isPendingUpdateQuote } = useMutation({
    mutationFn: updateQuote,
    onSuccess: (response) => {
      navigate(`/quotes/${response.id}?job_number=${jobNumber}`);
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsConfig] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.QuoteDetails, quoteId] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.JobReport, quoteId] }),
      ]);
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: error });
    },
  });

  const sanitizeCostPerUnit = () => {
    form.getValues("quoteDetails").forEach((item, index) => {
      if (item.cost_per_unit === undefined || Number.isNaN(item.cost_per_unit)) {
        form.setValue(`quoteDetails.${index}.cost_per_unit`, 0);
      }
    });
  };

  const handleSubmitForm = (isDraft: boolean) => {
    if (!isDraft) {
      setIsConfirmCreationOpen(true);
    } else {
      handleCreateQuote(isDraft);
    }
  };

  const getQuotePayload = () => {
    const quoteData = form.getValues();
    if (jobNumber) {
      const total_revenue =
        quoteData?.quoteDetails.reduce((total, item) => total + item.quantity * item.price_per_unit, 0) ?? 0;
      const total_cost =
        quoteData?.quoteDetails.reduce((total, item) => total + item.quantity * item.cost_per_unit, 0) ?? 0;
      const margin = getMargin({ totalQuotes: total_revenue, totalCosts: total_cost });
      return {
        quote_name: quoteData.contactInfo.name,
        contact_info_email: quoteData.contactInfo.email,
        job_number: jobNumber,
        salesman: quoteData.contactInfo.salesman,
        is_lump_sum: quoteData.isQuoteLumpSum,
        scope_description: quoteData.scopeDescription,
        quote_details: quoteData.quoteDetails.map((item) => {
          const total_revenue = item.price_per_unit * item.quantity;
          const total_cost = item.cost_per_unit * item.quantity;
          const margin = getMargin({ totalQuotes: total_revenue, totalCosts: total_cost });
          return {
            ...item,
            total_revenue,
            total_cost,
            margin,
          };
        }),
        additional_notes,
        new_notes: notes.filter((note) => {
          return !!note.note_name;
        }),
        total_cost,
        total_revenue,
        total_revenue_override: quoteData.totalRevenueOverride ?? null,
        cost_breakdown: quoteData.isQuoteLumpSum && quoteData.costBreakdownEnabled ? quoteData.costBreakdown : null,
        margin,
      };
    }
  };

  const handleCreateQuote = (isDraft: boolean) => {
    const quotePayload = getQuotePayload();
    if (quotePayload) {
      if (quoteId) {
        updateQuoteMutate({ id: quoteId, data: { ...quotePayload, is_draft: isDraft } });
      } else {
        createQuoteMutate({ ...quotePayload, is_draft: isDraft });
      }
    }
  };

  if (isLoadingQuote) {
    return null;
  }
  return (
    <>
      <FormProvider {...form}>
        <form className="py-2">
          <div className="flex items-center justify-between">
            <Typography className="text-primary-500" variant="text-lg" weight="semibold">
              {jobNumber}
            </Typography>
            <Button
              onClick={(e) => {
                e.preventDefault();
                sanitizeCostPerUnit();
                return form.handleSubmit(() => handleSubmitForm(false))();
              }}
              className="mr-2 ml-auto"
              disabled={isPendingCreateQuote || isPendingUpdateQuote}
            >
              {quoteId && !quote?.is_draft ? "Update quote" : "Create quote"}
            </Button>
            <Button
              disabled={isPendingCreateQuote || isPendingUpdateQuote}
              onClick={(e) => {
                e.preventDefault();
                sanitizeCostPerUnit();
                return form.handleSubmit(() => {
                  handleSubmitForm(true);
                })();
              }}
            >
              Save as draft
            </Button>
          </div>
          <Separator className="my-10" />
          <div className="flex flex-col gap-4">
            <QuoteDetailsTable
              isFetchingInitialQuoteData={isFetchingInitialQuoteData}
              repairSystemName={repairSystem?.name}
              repairSystemId={repairSystemId}
            />
            <ContactInfo />
            <ScopeDescription />
            <AdditionalNotes
              additional_notes={additional_notes}
              notes={notes}
              quote={quote}
              setAdditional_notes={setAdditional_notes}
              removeNote={removeNote}
              addNote={addNote}
              updateNote={updateNote}
            />
          </div>
        </form>
      </FormProvider>
      <ConfirmCreateQuote
        isOpen={isConfirmCreationOpen}
        setIsOpen={setIsConfirmCreationOpen}
        handleCreateQuote={handleCreateQuote}
        isPendingCreateQuote={isPendingCreateQuote}
      />
    </>
  );
}
